import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const toPayload = (a) => ({ name: a.name || "", icon: a.icon || "", sort_order: Number(a.sort_order) || 0, active: a.active !== false });

export default function AmenitiesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", icon: "", sort_order: 0 });

  const load = useCallback(() => http.get("/admin/amenities").then((r) => setItems(r.data.amenities)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return toast.error("Amenity name is required");
    try {
      await http.post("/admin/amenities", { ...form, sort_order: Number(form.sort_order) || 0 });
      toast.success("Amenity added");
      setForm({ name: "", icon: "", sort_order: 0 });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const save = async (a, patch) => {
    try {
      await http.put(`/admin/amenities/${a.id}`, { ...toPayload(a), ...patch });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="amenities-manager">
      <div className="card">
        <h3 className="font-serif text-2xl mb-4">Add Amenity</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <input data-testid="amenity-form-name" placeholder="Amenity name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="md:col-span-2" />
          <input data-testid="amenity-form-icon" placeholder="Icon (optional)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
          <input data-testid="amenity-form-order" type="number" placeholder="Order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
          <button data-testid="amenity-form-add" onClick={add} className="btn-primary flex items-center justify-center gap-2 md:col-span-4"><Plus size={16} /> Add Amenity</button>
        </div>
      </div>

      <div className="card">
        <h3 className="font-serif text-2xl mb-3">Amenities ({items.filter((i) => i.active).length} shown on website)</h3>
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-2 py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`amenity-row-${a.id}`}>
              <span className="font-medium flex-1 min-w-32">{a.name}{!a.active && <span className="badge-terracotta ml-2">hidden</span>}</span>
              <input data-testid={`amenity-order-${a.id}`} type="number" defaultValue={a.sort_order} key={`o-${a.id}-${a.sort_order}`}
                onBlur={(e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v !== a.sort_order) save(a, { sort_order: v }); }}
                style={{ width: 70, padding: "6px 10px" }} title="Display order" />
              <button data-testid={`amenity-rename-${a.id}`} onClick={() => {
                const name = window.prompt("Edit amenity name", a.name);
                if (name && name !== a.name) save(a, { name });
              }} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>Rename</button>
              <button data-testid={`amenity-toggle-${a.id}`} onClick={() => save(a, { active: !a.active })} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>
                {a.active ? "Hide" : "Show"}
              </button>
            </div>
          ))}
          {items.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No amenities yet.</p>}
        </div>
      </div>
    </div>
  );
}
